import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  Paper,
  Typography,
  LinearProgress,
  Collapse,
  IconButton,
  Chip,
  Grid,
} from "@material-ui/core";
import {
  ExpandLess,
  ExpandMore,
  CheckCircle,
  Error,
  CloudDownload,
  Close,
} from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";
import { green, red, blue, grey } from "@material-ui/core/colors";
import { AuthContext } from "../../context/Auth/AuthContext";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";

const useStyles = makeStyles((theme) => ({
  root: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    position: "relative",
  },
  rootImporting: {
    border: `2px solid ${blue[500]}`,
    boxShadow: `0 0 10px ${blue[200]}`,
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(1),
  },
  title: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  icon: {
    fontSize: 24,
  },
  iconImporting: {
    color: blue[500],
  },
  iconSuccess: {
    color: green[500],
  },
  iconError: {
    color: red[500],
  },
  progressBar: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    height: 8,
    borderRadius: 4,
  },
  stats: {
    display: "flex",
    gap: theme.spacing(2),
    flexWrap: "wrap",
    marginTop: theme.spacing(1),
  },
  chip: {
    fontWeight: 500,
  },
  details: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.action.hover,
    borderRadius: theme.shape.borderRadius,
  },
  timestamp: {
    color: theme.palette.text.secondary,
    fontSize: "0.75rem",
    marginTop: theme.spacing(1),
  },
  closeButton: {
    padding: theme.spacing(0.5),
  },
}));

const ContactImportMonitor = () => {
  const classes = useStyles();
  const { user, socket } = useContext(AuthContext);
  const companyId = user?.companyId;

  const [importProgress, setImportProgress] = useState(null);
  const [lastTaskSummary, setLastTaskSummary] = useState(null);
  const [isExpanded, setIsExpanded] = useState(true);
  const [isVisible, setIsVisible] = useState(false);
  const [error, setError] = useState(null);
  const [lastEventTime, setLastEventTime] = useState(null);
  const [estimatedTime, setEstimatedTime] = useState(null);

  // Buscar jobs ativos no carregamento da página
  useEffect(() => {
    const fetchActiveJobs = async () => {
      try {
        const { data } = await api.get("/contacts/import-job/active");

        if (data.hasActiveJobs && data.jobs.length > 0) {
          const activeJob = data.jobs[0]; // Pegar primeiro job ativo

          // Simular evento de progresso inicial
          setImportProgress({
            fileName: activeJob.fileName,
            processed: activeJob.processedContacts,
            created: activeJob.createdContacts || 0,
            updated: activeJob.updatedContacts || 0,
            errors: activeJob.errorContacts || 0,
            total: activeJob.totalContacts,
            totalImported: activeJob.processedContacts,
            completed: activeJob.status === "completed",
            timestamp: new Date().toISOString(),
          });

          setIsVisible(true);
          console.log("[ContactImportMonitor] Job ativo encontrado no carregamento:", activeJob);
        }
      } catch (err) {
        console.error("[ContactImportMonitor] Erro ao buscar jobs ativos:", err);
      }
    };

    if (companyId) {
      fetchActiveJobs();
    }
  }, [companyId]);

  useEffect(() => {
    if (!socket || !companyId) return;

    // Nomes dos eventos seguindo o padrão: company-{companyId}-eventName
    const batchProgressEvent = `company-${companyId}-contact-import:batch-progress`;
    const taskCompleteEvent = `company-${companyId}-contact-import:task-complete`;
    const errorEvent = `company-${companyId}-contact-import:error`;

    // Evento: Progresso do lote
    const handleBatchProgress = (data) => {
      console.log("[ContactImportMonitor] Batch progress:", data);

      const now = Date.now();

      // Calcular tempo estimado baseado no tempo entre lotes
      if (lastEventTime && data.processed > 0 && !data.completed) {
        const timeSinceLastEvent = (now - lastEventTime) / 1000; // segundos
        const remainingContacts = data.total - data.totalImported;
        const contactsPerBatch = data.processed;

        if (remainingContacts > 0 && contactsPerBatch > 0) {
          const estimatedBatches = Math.ceil(remainingContacts / contactsPerBatch);
          const estimatedSeconds = estimatedBatches * timeSinceLastEvent;
          setEstimatedTime(estimatedSeconds);
        }
      }

      setLastEventTime(now);
      setImportProgress(data);
      setIsVisible(true);
      setError(null);

      // Se completou, mantém visível por 5 segundos
      if (data.completed) {
        setEstimatedTime(null);
        setTimeout(() => {
          setImportProgress(null);
        }, 5000);
      }
    };

    // Evento: Conclusão da task
    const handleTaskComplete = (data) => {
      console.log("[ContactImportMonitor] Task complete:", data);
      setLastTaskSummary(data.summary);

      // Se não há mais progresso ativo, esconde após 10 segundos
      if (!importProgress || importProgress.completed) {
        setTimeout(() => {
          setIsVisible(false);
          setLastTaskSummary(null);
        }, 10000);
      }
    };

    // Evento: Erro
    const handleError = (data) => {
      console.error("[ContactImportMonitor] Import error:", data);
      setError(data);
      setIsVisible(true);

      // Esconde erro após 30 segundos
      setTimeout(() => {
        setError(null);
      }, 30000);
    };

    console.log("[ContactImportMonitor] Registrando listeners para:", {
      batchProgressEvent,
      taskCompleteEvent,
      errorEvent
    });

    socket.on(batchProgressEvent, handleBatchProgress);
    socket.on(taskCompleteEvent, handleTaskComplete);
    socket.on(errorEvent, handleError);

    return () => {
      socket.off(batchProgressEvent, handleBatchProgress);
      socket.off(taskCompleteEvent, handleTaskComplete);
      socket.off(errorEvent, handleError);
    };
  }, [socket, companyId, importProgress]);

  // Não mostrar se não há dados
  if (!isVisible && !importProgress && !lastTaskSummary && !error) {
    return null;
  }

  const handleClose = () => {
    setIsVisible(false);
    setImportProgress(null);
    setLastTaskSummary(null);
    setError(null);
    setEstimatedTime(null);
    setLastEventTime(null);
  };

  // Calcular porcentagem (usar totalImported para mostrar progresso acumulado)
  const percentage = importProgress
    ? Math.round((importProgress.totalImported / importProgress.total) * 100)
    : 0;

  // Formatar tempo estimado
  const formatEstimatedTime = (seconds) => {
    if (!seconds || seconds <= 0) return null;

    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);

    if (minutes > 0) {
      return `${minutes}min ${remainingSeconds}s`;
    }
    return `${remainingSeconds}s`;
  };

  // Determinar status
  const isImporting = importProgress && !importProgress.completed;
  const isCompleted = importProgress?.completed || false;
  const hasError = !!error;

  return (
    <Paper
      className={`${classes.root} ${isImporting ? classes.rootImporting : ""}`}
      elevation={3}
    >
      <Box className={classes.header}>
        <Box className={classes.title}>
          {hasError ? (
            <Error className={`${classes.icon} ${classes.iconError}`} />
          ) : isCompleted ? (
            <CheckCircle className={`${classes.icon} ${classes.iconSuccess}`} />
          ) : (
            <CloudDownload className={`${classes.icon} ${classes.iconImporting}`} />
          )}
          <Typography variant="h6">
            {hasError
              ? i18n.t("contacts.import.error")
              : isCompleted
              ? i18n.t("contacts.import.completed")
              : i18n.t("contacts.import.importing")}
          </Typography>
        </Box>

        <Box display="flex" alignItems="center">
          <IconButton
            size="small"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            {isExpanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
          <IconButton
            size="small"
            className={classes.closeButton}
            onClick={handleClose}
          >
            <Close fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      <Collapse in={isExpanded}>
        {/* Erro */}
        {hasError && (
          <Box>
            <Typography color="error" variant="body2">
              <strong>{i18n.t("contacts.import.fileName")}:</strong> {error.fileName}
            </Typography>
            <Typography color="error" variant="body2">
              <strong>{i18n.t("contacts.import.errorMessage")}:</strong> {error.error}
            </Typography>
            <Typography className={classes.timestamp}>
              {new Date(error.timestamp).toLocaleString()}
            </Typography>
          </Box>
        )}

        {/* Progresso do lote */}
        {importProgress && (
          <Box>
            <Typography variant="body2" color="textSecondary">
              {i18n.t("contacts.import.processingFile")}: <strong>{importProgress.fileName}</strong>
            </Typography>

            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Typography variant="body2">
                {importProgress.totalImported} / {importProgress.total} {i18n.t("contacts.import.contacts")}
              </Typography>
              <Typography variant="body2" color="primary">
                <strong>{percentage}%</strong>
              </Typography>
            </Box>

            <Box display="flex" alignItems="center" justifyContent="space-between" style={{ marginTop: 4 }}>
              {importProgress.processed > 0 && (
                <Typography variant="caption" color="textSecondary">
                  (+{importProgress.processed})
                </Typography>
              )}
              {estimatedTime && !importProgress.completed && (
                <Typography variant="caption" color="primary" style={{ fontStyle: 'italic' }}>
                  ⏱️ {i18n.t("contacts.import.estimatedTime")}: ~{formatEstimatedTime(estimatedTime)}
                </Typography>
              )}
            </Box>

            <LinearProgress
              variant="determinate"
              value={percentage}
              className={classes.progressBar}
              color={isCompleted ? "primary" : "secondary"}
            />

            <Grid container spacing={1} className={classes.stats}>
              {(importProgress.created != null && importProgress.created > 0) && (
                <Grid item>
                  <Chip
                    label={`✅ ${i18n.t("contacts.import.created")}: ${importProgress.created}`}
                    size="small"
                    className={classes.chip}
                    style={{ backgroundColor: green[100], color: green[800] }}
                  />
                </Grid>
              )}
              {(importProgress.updated != null && importProgress.updated > 0) && (
                <Grid item>
                  <Chip
                    label={`🔄 ${i18n.t("contacts.import.updated")}: ${importProgress.updated}`}
                    size="small"
                    className={classes.chip}
                    style={{ backgroundColor: blue[100], color: blue[800] }}
                  />
                </Grid>
              )}
              {(importProgress.skipped != null && importProgress.skipped > 0) && (
                <Grid item>
                  <Chip
                    label={`⏭️ ${i18n.t("contacts.import.skipped")}: ${importProgress.skipped}`}
                    size="small"
                    className={classes.chip}
                    style={{ backgroundColor: grey[300], color: grey[800] }}
                  />
                </Grid>
              )}
              {(importProgress.errors != null && importProgress.errors > 0) && (
                <Grid item>
                  <Chip
                    label={`❌ ${i18n.t("contacts.import.errors")}: ${importProgress.errors}`}
                    size="small"
                    className={classes.chip}
                    style={{ backgroundColor: red[100], color: red[800] }}
                  />
                </Grid>
              )}
            </Grid>

            {isCompleted && (
              <Typography
                variant="body2"
                style={{ color: green[700], marginTop: 8, fontWeight: 500 }}
              >
                ✅ {i18n.t("contacts.import.fileCompleted")}
              </Typography>
            )}

            <Typography className={classes.timestamp}>
              {new Date(importProgress.timestamp).toLocaleString()}
            </Typography>
          </Box>
        )}

        {/* Resumo da última task
        {lastTaskSummary && !importProgress && (
          <Box className={classes.details}>
            <Typography variant="subtitle2" gutterBottom>
              {i18n.t("contacts.import.lastTaskSummary")}
            </Typography>

            <Grid container spacing={1}>
              <Grid item xs={6} sm={4}>
                <Typography variant="body2">
                  <strong>{i18n.t("contacts.import.totalProcessed")}:</strong>{" "}
                  {lastTaskSummary.totalProcessed}
                </Typography>
              </Grid>
              {(lastTaskSummary.totalCreated != null && lastTaskSummary.totalCreated > 0) && (
                <Grid item xs={6} sm={4}>
                  <Typography variant="body2" style={{ color: green[700] }}>
                    <strong>{i18n.t("contacts.import.created")}:</strong>{" "}
                    {lastTaskSummary.totalCreated}
                  </Typography>
                </Grid>
              )}
              {(lastTaskSummary.totalUpdated != null && lastTaskSummary.totalUpdated > 0) && (
                <Grid item xs={6} sm={4}>
                  <Typography variant="body2" style={{ color: blue[700] }}>
                    <strong>{i18n.t("contacts.import.updated")}:</strong>{" "}
                    {lastTaskSummary.totalUpdated}
                  </Typography>
                </Grid>
              )}
              {(lastTaskSummary.totalSkipped != null && lastTaskSummary.totalSkipped > 0) && (
                <Grid item xs={6} sm={4}>
                  <Typography variant="body2" style={{ color: grey[700] }}>
                    <strong>{i18n.t("contacts.import.skipped")}:</strong>{" "}
                    {lastTaskSummary.totalSkipped}
                  </Typography>
                </Grid>
              )}
              {(lastTaskSummary.totalErrors != null && lastTaskSummary.totalErrors > 0) && (
                <Grid item xs={6} sm={4}>
                  <Typography variant="body2" style={{ color: red[700] }}>
                    <strong>{i18n.t("contacts.import.errors")}:</strong>{" "}
                    {lastTaskSummary.totalErrors}
                  </Typography>
                </Grid>
              )}
            </Grid>

            {lastTaskSummary.filesCompleted.length > 0 && (
              <Box mt={1}>
                <Typography variant="body2">
                  <strong>{i18n.t("contacts.import.completedFiles")}:</strong>
                </Typography>
                {lastTaskSummary.filesCompleted.map((file, index) => (
                  <Chip
                    key={index}
                    label={file}
                    size="small"
                    style={{
                      marginRight: 4,
                      marginTop: 4,
                      backgroundColor: grey[200],
                    }}
                  />
                ))}
              </Box>
            )}
          </Box>
        )} */}
      </Collapse>
    </Paper>
  );
};

export default ContactImportMonitor;
